import React, { useState, useContext } from 'react'
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    LinearProgress,
    Alert,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { UserContext } from '../../contexts/UserContext'
import axios from 'axios'
import { getBackendUserUrl } from '../../utils/utils'

const DocumentUploader = ({ onUploadComplete, eventId, setDocuments }) => {
    const { user, hasRole } = useContext(UserContext)
    const [open, setOpen] = useState(false)
    const [file, setFile] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState(null)

    const handleClickOpen = () => {
        if (hasRole(['user', 'admin'])) {
            setOpen(true)
        } else {
            alert('Uploading documents is not yet available')
        }
    }

    const handleClose = () => {
        setOpen(false)
        setFile(null)
        setError(null)
    }

    const handleFileChange = (event) => {
        setFile(event.target.files[0])
    }

    const handleUpload = async () => {
        if (!file || !eventId) return
        setUploading(true)
        setError(null)
        try {
            const formData = new FormData()
            formData.append('file', file)

            // First request to extract entities from the document
            const extractUrl = `${getBackendUserUrl()}/event/${eventId}/extract_entities_from_document`
            const response = await axios.post(extractUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })

            // After extraction, store the document
            if (response.data) {
                const storeUrl = `${getBackendUserUrl()}/document/store_document_for_event?event_id=${eventId}`
                await axios.post(storeUrl, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                onUploadComplete(response.data)
                setDocuments((prevDocuments) => [...prevDocuments, response.data])
                handleClose()
            } else if (response.data && response.data.error) {
                setError(response.data.error)
            } else {
                setError('Failed to extract event details from document.')
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Upload failed')
        } finally {
            setUploading(false)
        }
    }

    // Don't render the component if user isn't logged in
    if (!user) return null

    return (
        <>
            <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={handleClickOpen}
                sx={{
                    height: '28px',
                    minWidth: '90px',
                    borderRadius: '100px',
                    padding: '6px 12px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    backgroundColor: '#4F6BFF',
                    color: '#FFFFFF',
                    '&:hover': {
                        backgroundColor: '#3D55DB',
                    },
                }}
            >
                Upload Event Documents
            </Button>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Upload Event Documents</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Upload a PDF, Word document, or Event Pitch deck to
                        automatically fill out event details.
                    </Typography>
                    <input
                        accept=".pdf,.doc,.docx,.ppt,.pptx"
                        style={{ display: 'none' }}
                        id="raised-button-file"
                        type="file"
                        onChange={handleFileChange}
                    />
                    <label htmlFor="raised-button-file">
                        <Button 
                            variant="outlined" 
                            component="span"
                            sx={{
                                height: '28px',
                                minWidth: '90px',
                                borderRadius: '100px',
                                padding: '6px 12px',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                border: '1.5px solid #4F6BFF',
                                color: '#4F6BFF',
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: 'rgba(79, 107, 255, 0.08)',
                                    borderColor: '#3D55DB',
                                },
                            }}
                        >
                            Choose File
                        </Button>
                    </label>
                    {file && (
                        <Typography variant="body2">{file.name}</Typography>
                    )}
                    {uploading && <LinearProgress sx={{ mt: 2 }} />}
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleClose}
                        sx={{
                            height: '28px',
                            minWidth: '70px',
                            borderRadius: '75px',
                            padding: '6px 12px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            border: '1.5px solid #4F6BFF',
                            color: '#4F6BFF',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                backgroundColor: 'rgba(79, 107, 255, 0.08)',
                                borderColor: '#3D55DB',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        sx={{
                            height: '28px',
                            minWidth: '70px',
                            borderRadius: '75px',
                            padding: '6px 12px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            backgroundColor: '#4F6BFF',
                            color: '#FFFFFF',
                            '&:hover': {
                                backgroundColor: '#3D55DB',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                color: 'rgba(0, 0, 0, 0.26)',
                            },
                        }}
                    >
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default DocumentUploader
